

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TeacherAvailabilityTemplate, WeeklySchedule } from '@/types/teacherAvailability';

export function useTeacherAvailability(teacherCode: string | undefined) {
  const { toast } = useToast();
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule>({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch weekly schedule  
  const fetchWeeklySchedule = async () => {
    if (!teacherCode) return;

    const { data, error } = await supabase
      .from('teacher_availability_templates')
      .select('*')
      .eq('teacher_code', teacherCode)
      .order('day_of_week')
      .order('slot_order');

    if (error) {
      console.error('Error fetching weekly schedule:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถโหลดตารางเวลาได้',
        variant: 'destructive'
      });
    } else {
      const schedule: WeeklySchedule = {};
      data?.forEach((item: TeacherAvailabilityTemplate) => {
        if (!schedule[item.day_of_week]) {
          schedule[item.day_of_week] = [];
        }
        schedule[item.day_of_week].push(item);
      });
      setWeeklySchedule(schedule);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await fetchWeeklySchedule();
      setIsLoading(false);
    };

    fetchData();
  }, [teacherCode]);

  // Add new time slot
  const addTimeSlot = async (dayOfWeek: number, scheduleData: {
    startTime: string;
    endTime: string;
  }) => {
    if (!teacherCode) return false;

    const existingSlots = weeklySchedule[dayOfWeek] || [];
    const nextSlotOrder = existingSlots.length > 0 ? Math.max(...existingSlots.map(s => s.slot_order)) + 1 : 1;

    try {
      const { error } = await supabase
        .from('teacher_availability_templates')
        .insert({
          teacher_code: teacherCode,
          day_of_week: dayOfWeek,
          start_time: scheduleData.startTime,
          end_time: scheduleData.endTime,
          slot_order: nextSlotOrder
        });

      if (error) throw error;

      await fetchWeeklySchedule();
      toast({
        title: 'สำเร็จ',
        description: 'เพิ่มช่วงเวลาแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error adding time slot:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเพิ่มช่วงเวลาได้',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Update time slot
  const updateTimeSlot = async (slotId: string, scheduleData: {
    startTime: string;
    endTime: string;
  }) => {
    try {
      const { error } = await supabase
        .from('teacher_availability_templates')
        .update({
          start_time: scheduleData.startTime,
          end_time: scheduleData.endTime
        })
        .eq('id', slotId);

      if (error) throw error;

      await fetchWeeklySchedule();
      toast({
        title: 'สำเร็จ',
        description: 'อัพเดทช่วงเวลาแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error updating time slot:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัพเดทช่วงเวลาได้',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Delete time slot
  const deleteTimeSlot = async (slotId: string) => {
    try {
      const { error } = await supabase
        .from('teacher_availability_templates')
        .delete()
        .eq('id', slotId);

      if (error) throw error;

      await fetchWeeklySchedule();
      toast({
        title: 'สำเร็จ',
        description: 'ลบช่วงเวลาแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error deleting time slot:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถลบช่วงเวลาได้',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Delete all slots for a day
  const deleteAllSlotsForDay = async (dayOfWeek: number) => {
    if (!teacherCode) return false;

    try {
      const { error } = await supabase
        .from('teacher_availability_templates')
        .delete()
        .eq('teacher_code', teacherCode)
        .eq('day_of_week', dayOfWeek);

      if (error) throw error;

      await fetchWeeklySchedule();
      toast({
        title: 'สำเร็จ',
        description: 'ลบตารางเวลาทั้งวันแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error deleting all slots for day:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถลบตารางเวลาได้',
        variant: 'destructive'
      });
      return false;
    }
  };

  return {
    weeklySchedule,
    isLoading,
    addTimeSlot,
    updateTimeSlot,
    deleteTimeSlot,
    deleteAllSlotsForDay,
    refetch: fetchWeeklySchedule
  };
}

